import onetime from "@-/com.npmjs.onetime";
import {
  PINYIN_KEYBOARD_ROW_LIST,
  type PinyinKey,
} from "@-/dev.improve.haniverse.routes.nav.app._components.pinyin-keyboard._layout";
import { getPinyinSyllablePrefixSet } from "#get-pinyin-syllable-prefix-set";

const LETTER_QWERTY_KEY_REGEX = /^[a-z]$/u;

const ROUND_LIP_INITIAL_SET = new Set(["g", "k", "h", "zh", "ch", "sh"]);
/** Initials that take `iong` (`yong` is spelled with `ong`, so `y` is excluded). */
const JQX_INITIAL_SET = new Set(["j", "q", "x"]);
/** Initials that take a bare `o` rather than `uo` (`bo`, `wo`, `yo`; but `duo`, `luo`). */
const O_FINAL_INITIAL_SET = new Set(["", "b", "p", "m", "f", "w", "y"]);
/** Initials that combine with `ü`; after j/q/x/y it is spelled `u` (`ju`, `yue`), after n/l it stays `ü` (`nü`, `lüe`). */
const U_UMLAUT_INITIAL_SET = new Set(["n", "l", "j", "q", "x", "y"]);
const U_UMLAUT_SPELLED_AS_U_INITIAL_SET = new Set(["j", "q", "x", "y"]);

/**
 * Xiaohe zero-initial syllables (零声母): every syllable in Shuangpin is two keystrokes,
 * so syllables that don't start with a consonant use the first vowel as the "initial" and
 * a second key as the "final". The mapping below is keyed by `<first><second>` qwerty letters:
 *
 * - One-letter syllables (a/o/e) repeat the key: aa → a, oo → o, ee → e.
 * - Two-letter syllables (ai/an/ao/ei/en/er/ou) type literally: ai → ai, en → en, er → er, etc.
 * - Three-letter syllables use Xiaohe's final-key assignments: ang → ah (h is the ang key),
 *   eng → eg (g is the eng key).
 */
const XIAOHE_ZERO_INITIAL_KEY_SET: ReadonlySet<string> = new Set(["a", "e", "o"]);
const XIAOHE_ZERO_INITIAL_MAP: Readonly<Record<string, string>> = {
  aa: "a",
  ah: "ang",
  ai: "ai",
  an: "an",
  ao: "ao",
  ee: "e",
  eg: "eng",
  ei: "ei",
  en: "en",
  er: "er",
  oo: "o",
  ou: "ou",
};

const getLetterKeyByQwertyKey = onetime(
  (): ReadonlyMap<string, PinyinKey> =>
    new Map(
      PINYIN_KEYBOARD_ROW_LIST.flat()
        .filter((key) => LETTER_QWERTY_KEY_REGEX.test(key.qwertyKey))
        .map((key) => [key.qwertyKey, key]),
    ),
);

export function getLetterKey(qwertyKey: string): PinyinKey | undefined {
  return getLetterKeyByQwertyKey().get(qwertyKey);
}

function resolveDefaultFinal({
  finalKey,
  finalList,
  initial,
}: {
  finalKey: string;
  finalList: readonly string[];
  initial: string;
}): string {
  if (finalKey === "uo/o") {
    return O_FINAL_INITIAL_SET.has(initial) ? "o" : "uo";
  }
  if (finalKey === "iong/ong") {
    return JQX_INITIAL_SET.has(initial) ? "iong" : "ong";
  }
  if (finalKey === "ing/uai") {
    return ROUND_LIP_INITIAL_SET.has(initial) ? "uai" : "ing";
  }
  if (finalKey === "iang/uang") {
    return ROUND_LIP_INITIAL_SET.has(initial) ? "uang" : "iang";
  }
  if (finalKey === "ia/ua") {
    return ROUND_LIP_INITIAL_SET.has(initial) ? "ua" : "ia";
  }
  if (finalKey === "ü/ui") {
    return U_UMLAUT_INITIAL_SET.has(initial) ? "ü" : "ui";
  }
  const firstFinal = finalList[0];
  if (firstFinal === undefined) {
    throw new Error("resolveDefaultFinal received an empty finalList");
  }
  return firstFinal;
}

/** Joins an initial and final using standard pinyin orthography (`j` + `ü` → `ju`, `n` + `ü` → `nü`). */
function spellSyllable({ final_, initial }: { final_: string; initial: string }): string {
  if (final_.startsWith("ü") && U_UMLAUT_SPELLED_AS_U_INITIAL_SET.has(initial)) {
    return `${initial}u${final_.slice(1)}`;
  }
  return `${initial}${final_}`;
}

export function resolveShuangpinSyllable({
  finalList,
  initial,
  validPinyinSet,
}: {
  finalList: readonly string[];
  initial: string;
  validPinyinSet?: ReadonlySet<string>;
}): string | null {
  if (validPinyinSet) {
    return (
      finalList
        .map((final_) => spellSyllable({ final_, initial }))
        .find((candidate) => validPinyinSet.has(candidate)) ?? null
    );
  }

  const finalKey = finalList.join("/");
  const defaultFinal = resolveDefaultFinal({ finalKey, finalList, initial });

  return defaultFinal ? spellSyllable({ final_: defaultFinal, initial }) : null;
}

/** Reads two letter keystrokes as one Xiaohe syllable (`vs` → `zhong`, `ah` → `ang`). */
function resolveTwoKeystrokeShuangpin({
  keystrokes,
  validPinyinSet,
}: {
  keystrokes: string;
  validPinyinSet?: ReadonlySet<string>;
}): string | null {
  const [firstQwertyKey = "", secondQwertyKey = ""] = keystrokes;
  if (XIAOHE_ZERO_INITIAL_KEY_SET.has(firstQwertyKey)) {
    const pinyin = XIAOHE_ZERO_INITIAL_MAP[keystrokes];
    if (!pinyin || (validPinyinSet && !validPinyinSet.has(pinyin))) {
      return null;
    }
    return pinyin;
  }
  const firstKey = getLetterKey(firstQwertyKey);
  const secondKey = getLetterKey(secondQwertyKey);
  if (!firstKey || !secondKey || !("initial" in firstKey)) {
    return null;
  }
  return resolveShuangpinSyllable({
    finalList: secondKey.finalList,
    initial: firstKey.initial,
    validPinyinSet,
  });
}

/**
 * What the pinyin field shows for the letter keys tapped so far, or `null` when they can't form a
 * syllable (the caller then starts over from the newest key).
 *
 * - One key is shown as typed.
 * - Two keys are read as Xiaohe shuangpin (`vs` → `zhong`) when `shuangpinEnabled`. Every two-letter
 *   full-pinyin syllable except `lo` (read as `luo`) comes out unchanged, so typing full pinyin
 *   still works.
 * - Three or more keys, or two keys that aren't a shuangpin syllable, are read letter by letter as
 *   long as they could still become a syllable (`zho` on the way to `zhong`).
 */
export function interpretKeystrokes({
  keystrokes,
  shuangpinEnabled,
  validPinyinSet,
}: {
  keystrokes: string;
  shuangpinEnabled: boolean;
  validPinyinSet?: ReadonlySet<string>;
}): string | null {
  if (keystrokes.length === 1) {
    return keystrokes;
  }
  if (shuangpinEnabled && keystrokes.length === 2) {
    const shuangpin = resolveTwoKeystrokeShuangpin({ keystrokes, validPinyinSet });
    if (shuangpin !== null) {
      return shuangpin;
    }
  }
  return getPinyinSyllablePrefixSet().has(keystrokes) ? keystrokes : null;
}
